import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MessageCircle, Send, Mail, Phone, Facebook, Instagram } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { resolveImg } from "@/lib/api";

const initials = (name) => (name || "E").split(" ").filter(Boolean).map((n) => n[0]).slice(0, 2).join("");
// A social link is "real" only when it is a proper URL (not blank / "#" / the old placeholders).
const isRealLink = (url) => !!url && url !== "#" && /^https?:\/\//i.test(url) && !/yourpage|yourhandle/i.test(url);

export const Contact = ({ contact = {}, config = {} }) => {
  const c = config || {};
  return (
    <section id="contact" className="scroll-anchor bg-background">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal>
          <Card className="overflow-hidden rounded-2xl border-border bg-card shadow-[0_18px_50px_-26px_rgba(15,23,42,0.35)]">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="bg-primary p-8 text-primary-foreground sm:p-10">
                <p className="text-sm font-semibold uppercase tracking-wider text-accent">{contact.eyebrow}</p>
                <h2 className="font-display mt-2 text-3xl font-semibold tracking-tight">{contact.heading}</h2>
                <p className="mt-4 text-sm leading-relaxed text-primary-foreground/80">{contact.body}</p>
                <div className="mt-8 flex items-center gap-4">
                  <Avatar className="h-16 w-16 border-2 border-white/30 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.45)]" data-testid="contact-distributor-avatar">
                    {c.distributorPhoto ? (
                      <AvatarImage src={resolveImg(c.distributorPhoto)} alt={c.distributorName || "Distributor"} className="object-cover" data-testid="contact-distributor-photo" />
                    ) : null}
                    <AvatarFallback className="bg-accent text-accent-foreground">{initials(c.distributorName)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-base font-semibold" data-testid="contact-distributor-name">{c.distributorName || "Sam C. Ortega"}</p>
                    <p className="text-sm text-primary-foreground/70">{c.role || "Independent Distributor"}</p>
                    {c.location ? <p className="mt-0.5 text-xs text-primary-foreground/60" data-testid="contact-distributor-location">{c.location}</p> : null}
                  </div>
                </div>
              </div>

              <div className="p-8 sm:p-10">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Button asChild className="h-12 justify-start gap-3 bg-[hsl(152_60%_36%)] text-white hover:bg-[hsl(152_60%_32%)]">
                    <a href={c.whatsapp || "#"} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button"><MessageCircle className="h-5 w-5" /> WhatsApp</a>
                  </Button>
                  <Button asChild className="h-12 justify-start gap-3 bg-[hsl(211_100%_50%)] text-white hover:bg-[hsl(211_100%_45%)]">
                    <a href={c.messenger || "#"} target="_blank" rel="noopener noreferrer" data-testid="contact-messenger-button"><Send className="h-5 w-5" /> Messenger</a>
                  </Button>
                  <Button asChild variant="outline" className="h-12 justify-start gap-3">
                    <a href={c.email ? `mailto:${c.email}` : "#"} data-testid="contact-email-button"><Mail className="h-5 w-5" /> Email</a>
                  </Button>
                  <Button asChild variant="outline" className="h-12 justify-start gap-3">
                    <a href={c.phone ? `tel:${c.phone.replace(/\s/g, "")}` : "#"} data-testid="contact-call-button"><Phone className="h-5 w-5" /> Tawag</a>
                  </Button>
                </div>

                {(isRealLink(c.facebook) || isRealLink(c.instagram)) && (
                  <div className="mt-6 flex items-center gap-3 border-t border-border pt-6">
                    <span className="text-sm text-muted-foreground">Follow:</span>
                    {isRealLink(c.facebook) && (
                      <a href={c.facebook} target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-muted" aria-label="Facebook" data-testid="contact-facebook-link"><Facebook className="h-4 w-4" /></a>
                    )}
                    {isRealLink(c.instagram) && (
                      <a href={c.instagram} target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-muted" aria-label="Instagram" data-testid="contact-instagram-link"><Instagram className="h-4 w-4" /></a>
                    )}
                  </div>
                )}
                <p className="mt-6 text-xs text-muted-foreground">{c.phone} • {c.email}</p>
              </div>
            </div>
          </Card>
        </Reveal>
      </div>
    </section>
  );
};
